import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { money } from "@/lib/roof";
import { useI18n } from "@/i18n/I18nContext";
import { dateCourte } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Ruler, FileText, Trash2, MapPin, Loader2, Copy, Layers, List } from "lucide-react";
import { toast } from "sonner";

/**
 * Liste complète des estimations (rubrique « Vos estimations »).
 *
 * Un interrupteur bascule entre « tout afficher » et « une seule par adresse » :
 * en mode groupé, seule l'estimation la plus récente de chaque adresse est
 * montrée (la liste arrive déjà triée du plus récent au plus ancien), avec un
 * compteur du nombre de versions pour cette adresse.
 */
export default function Estimations() {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const fr = lang !== "en";
  const [deleting, setDeleting] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  // Défaut : tout afficher. Le bouton bascule vers le regroupement par adresse.
  const [grouper, setGrouper] = useState(false);

  const { data: estimations = [], isLoading, refetch } = useQuery({
    queryKey: ["estimates"],
    queryFn: async () => (await api.get("/estimates")).data,
  });

  const remove = async (id) => {
    if (!window.confirm(fr ? "Supprimer cette estimation ?" : "Delete this estimate?")) return;
    setDeleting(id);
    try {
      await api.delete(`/estimates/${id}`);
      toast.success(fr ? "Estimation supprimée" : "Estimate deleted");
      refetch();
    } catch {
      toast.error(fr ? "Échec de la suppression" : "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const duplicate = async (id) => {
    setDuplicating(id);
    try {
      const { data } = await api.post(`/estimates/${id}/duplicate`);
      toast.success(fr ? "Estimation dupliquée" : "Estimate duplicated");
      navigate(`/estimate/${data.estimate_id}`);
    } catch {
      toast.error(fr ? "Échec de la duplication" : "Duplicate failed");
    } finally {
      setDuplicating(null);
    }
  };

  // Clé d'adresse normalisée : casse et espaces ignorés, pour que « 12 Rue X »
  // et « 12  rue x » comptent comme la même propriété.
  const cleAdresse = (a) => (a || "").trim().toLowerCase().replace(/\s+/g, " ");

  // Nombre d'estimations par adresse (pour le badge « ×N » en mode groupé).
  const comptesParAdresse = useMemo(() => {
    const m = new Map();
    estimations.forEach((e) => {
      const k = cleAdresse(e.property_address);
      m.set(k, (m.get(k) || 0) + 1);
    });
    return m;
  }, [estimations]);

  // Liste affichée : toutes, ou la plus récente par adresse (déjà triées desc).
  const affichees = useMemo(() => {
    if (!grouper) return estimations;
    const vues = new Set();
    const out = [];
    estimations.forEach((e) => {
      const k = cleAdresse(e.property_address);
      if (vues.has(k)) return;
      vues.add(k);
      out.push(e);
    });
    return out;
  }, [estimations, grouper]);

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-2">
              {fr ? "Historique" : "History"}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter">
              {fr ? "Soumission" : "Quotes"}{" "}
              <span className="text-ink-faint font-normal text-2xl">
                ({affichees.length}{grouper && affichees.length !== estimations.length ? ` / ${estimations.length}` : ""})
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Interrupteur d'affichage : tout, ou une occurrence par adresse. */}
            <Button
              variant="outline"
              onClick={() => setGrouper((v) => !v)}
              data-testid="toggle-grouper"
              className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink"
            >
              {grouper ? <List className="h-4 w-4 mr-2" /> : <Layers className="h-4 w-4 mr-2" />}
              {grouper ? (fr ? "Tout afficher" : "Show all") : (fr ? "Une par adresse" : "One per address")}
            </Button>
            <Button onClick={() => navigate("/new")} data-testid="estimations-new" className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
              <Ruler className="h-4 w-4 mr-2" /> {fr ? "Nouvelle soumission" : "New quote"}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-ink" /></div>
        ) : estimations.length === 0 ? (
          <div className="bg-surface border border-line rounded-md p-12 text-center">
            <Ruler className="h-10 w-10 text-ink-faint mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">{fr ? "Aucune soumission" : "No quotes yet"}</h3>
            <p className="text-ink-faint mb-6">
              {fr ? "Créez votre première soumission à partir d'une adresse ou d'une mesure." : "Create your first quote from an address or a measurement."}
            </p>
            <Button onClick={() => navigate("/new")} className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
              <Ruler className="h-4 w-4 mr-2" /> {fr ? "Commencer" : "Get started"}
            </Button>
          </div>
        ) : (
          <div className="bg-surface border border-line rounded-md overflow-hidden" data-testid="estimations-table">
            {affichees.map((e) => {
              const nb = comptesParAdresse.get(cleAdresse(e.property_address)) || 1;
              return (
                <div key={e.estimate_id} className="flex items-center gap-4 px-5 py-4 border-b border-line last:border-0 hover:bg-hover transition-colors">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/estimate/${e.estimate_id}`)} data-testid={`estimation-row-${e.estimate_id}`}>
                    <div className="flex items-center gap-2 text-sm font-semibold truncate">
                      <MapPin className="h-3.5 w-3.5 text-brand-ink shrink-0" />
                      <span className="truncate">{e.property_address}</span>
                      {grouper && nb > 1 && (
                        <span
                          title={fr ? `${nb} estimations pour cette adresse` : `${nb} estimates for this address`}
                          className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold rounded-md bg-orange-500/15 text-brand-ink border border-orange-500/30"
                        >
                          ×{nb}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-ink-faint mt-1">
                      {e.client_name ? `${e.client_name} · ` : ""}
                      {e.total_squares?.toFixed(1)} {fr ? "carrés" : "sq"} · {dateCourte(e.created_at, lang)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-bold text-lg text-brand-ink">{money(e.total)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => duplicate(e.estimate_id)} data-testid={`duplicate-${e.estimate_id}`} title={fr ? "Dupliquer" : "Duplicate"} className="p-2 text-ink-soft hover:text-brand-ink rounded-md hover:bg-hover-strong transition-colors">
                      {duplicating === e.estimate_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                    </button>
                    <a href={`${api.defaults.baseURL}/estimates/${e.estimate_id}/pdf`} target="_blank" rel="noreferrer" data-testid={`pdf-${e.estimate_id}`} title="PDF" className="p-2 text-ink-soft hover:text-ink rounded-md hover:bg-hover-strong transition-colors">
                      <FileText className="h-4 w-4" />
                    </a>
                    <button onClick={() => remove(e.estimate_id)} data-testid={`delete-${e.estimate_id}`} title={fr ? "Supprimer" : "Delete"} className="p-2 text-ink-soft hover:text-red-400 rounded-md hover:bg-hover-strong transition-colors">
                      {deleting === e.estimate_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
